import { useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { PromoBannerSlot } from '@onesource/components';
import type { TLessonBase } from '@onesource/schemas';
import { LessonStatus, PromoBannerPosition, SpaceStatus } from '@onesource/schemas';
import {
  useGetCategories,
  useGetLessons,
  useGetModuleProgress,
  useGetSpace,
  useGetSubLessons,
} from '@onesource/services';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import { enqueueSnackbar } from 'notistack';
import { useEffect } from 'react';
import { z } from 'zod';

import { hasHTML } from '#/utils/has-html';

import { DraftBadge } from '../../../../../../../features/community-hub/components/draft-badge';
import { ModuleDetailSkeleton } from '../../../../../../../features/community-hub/components/module-detail-skeleton';
import { ModuleSectionCard } from '../../../../../../../features/community-hub/components/module-section-card';
import { usePreviewMode } from '../../../../../../../features/community-hub/hooks/use-preview-mode';

/**
 * Module Detail Route
 * Path: /community-hub/spaces/:id/modules/:moduleId
 * Shows detailed view of a specific module (CONTENT sub-space)
 * Redesigned based on Figma with accordion sections
 */
export const Route = createFileRoute('/_protected/community-hub/spaces/$id/modules/$moduleId/')({
  validateSearch: z.object({
    sectionId: z.string().optional().catch(undefined),
  }),
  component: ModuleDetailRoute,
});

/**
 * Section Data Loader Component
 * Loads sub-lessons and categories for a section
 */
interface SectionLoaderProps {
  section: TLessonBase;
  index: number;
  parentSpaceId: string;
  moduleId: string;
  expandedSectionId?: string;
  showSectionLabel?: boolean;
  showLessonLabel?: boolean;
}

const SectionLoader = ({
  section,
  index,
  parentSpaceId,
  moduleId,
  expandedSectionId,
  showSectionLabel,
  showLessonLabel,
}: SectionLoaderProps) => {
  const { data: subLessons, isLoading: isLoadingSubLessons } = useGetSubLessons(section.id);
  const { data: categories, isLoading: isLoadingCategories } = useGetCategories(section.id);
  const { isPreview } = usePreviewMode();
  const isLoading = isLoadingSubLessons || isLoadingCategories;

  // Filter to only published sub-lessons — unless the reviewer is previewing drafts.
  const visibleSubLessons = isPreview
    ? subLessons || []
    : subLessons?.filter(l => l.status === LessonStatus.PUBLISHED) || [];

  return (
    <ModuleSectionCard
      section={section}
      index={index}
      subLessons={isLoading ? [] : visibleSubLessons}
      categories={isLoading ? [] : categories || []}
      parentSpaceId={parentSpaceId}
      moduleId={moduleId}
      expandedSectionId={expandedSectionId}
      showSectionLabel={showSectionLabel}
      showLessonLabel={showLessonLabel}
    />
  );
};

function ModuleDetailRoute() {
  const { id: parentSpaceId, moduleId } = Route.useParams();
  const { sectionId: expandedSectionId } = Route.useSearch();
  const { isPreview } = usePreviewMode();
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: parentSpace, isLoading: isLoadingParent, error: parentError } = useGetSpace(parentSpaceId);
  const { data: module, isLoading: isLoadingModule, error: moduleError } = useGetSpace(moduleId);
  const { data: lessons, isLoading: isLoadingLessons, error: lessonsError } = useGetLessons(moduleId);
  const { data: moduleProgress } = useGetModuleProgress(moduleId);

  // The API gates a locked module's content (403 MODULE_LOCKED). Redirect back
  // to the space home with a message instead of showing a generic error.
  useEffect(() => {
    const data = (lessonsError as { response?: { data?: { code?: string } } } | null)?.response?.data;
    if (data?.code === 'MODULE_LOCKED') {
      enqueueSnackbar('Complete the prerequisite module to unlock this one.', { variant: 'info' });
      navigate({ to: '/community-hub/spaces/$id', params: { id: parentSpaceId } });
    }
  }, [lessonsError, navigate, parentSpaceId]);

  const isLoading = isLoadingParent || isLoadingModule || isLoadingLessons;

  if (isLoading) {
    return <ModuleDetailSkeleton />;
  }

  if (parentError || moduleError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load module: {parentError?.message || moduleError?.message}</Alert>
      </Box>
    );
  }

  if (!parentSpace || !module) {
    return (
      <Box p={3}>
        <Alert severity="error">Module not found</Alert>
      </Box>
    );
  }

  const progressPercent = moduleProgress?.progressPercent ?? 0;

  // Filter to only published lessons (sections) — unless the reviewer is previewing drafts.
  const visibleSections = isPreview ? lessons || [] : lessons?.filter(l => l.status === LessonStatus.PUBLISHED) || [];

  // Sanitize description HTML
  const sanitizedDescription = module.description ? DOMPurify.sanitize(module.description) : '';

  return (
    <Box sx={{ minHeight: 'calc(100vh - 180px)', overflow: { xs: 'visible', md: 'auto' } }}>
      {/* Header Section */}
      <Box sx={{ pb: 2 }}>
        {/* Breadcrumb */}
        <Breadcrumbs
          sx={{
            mb: 2,
            fontFamily: '"Figtree", sans-serif',
            fontSize: 16,
            lineHeight: '22px',
            fontWeight: 400,
            color: theme => theme.palette.grey[600],
            '& .MuiBreadcrumbs-separator': {
              mx: 0.6,
            },
          }}
        >
          <Link
            to="/community-hub/spaces/$id"
            params={{ id: parentSpaceId }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            <Typography
              variant="body2"
              sx={{ mt: hasHTML(parentSpace.name) ? -0.5 : 0, fontSize: 16, color: theme.palette.grey[600] }}
            >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parentSpace.name) }} />
            </Typography>
          </Link>
          {' / '}
          <Box component="span" sx={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(module.name) }} />
            {isPreview && module.status === SpaceStatus.DRAFT && <DraftBadge />}
          </Box>
        </Breadcrumbs>

        {/* Promo banner — header */}
        <PromoBannerSlot
          sx={{ width: { xs: '100%', md: 'calc(100% - 205px)' } }}
          entityId={moduleId}
          position={PromoBannerPosition.HEADER}
        />

        {module.showProgressBar !== false && (
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              maxWidth: { xs: '100%', md: 'calc(100% - 205px)' },
            }}
          >
            {/* Progress Percentage */}
            <Typography
              sx={{
                fontFamily: '"Fraunces", serif',
                fontWeight: 400,
                fontSize: 16.6,
                lineHeight: '24px',
                color: '#2e2e2d',
              }}
            >
              {progressPercent}%
            </Typography>

            {/* Progress Bar */}
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                width: '100%',
                height: 10,
                borderRadius: 3,
                bgcolor: 'rgba(0, 0, 0, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  bgcolor: '#c45569',
                },
              }}
            />
          </Box>
        )}

        {/* Description */}
        {sanitizedDescription && (
          <Box
            sx={{
              maxWidth: { xs: '100%', md: 'calc(100% - 315px)' },
              fontFamily: '"Figtree", sans-serif',
              fontWeight: 400,
              fontSize: 14,
              lineHeight: '19px',
              color: 'black',
              mb: { xs: 2, md: 3 },
              '& strong, & b': {
                fontWeight: 700,
              },
              '& p': {
                margin: 0,
                marginBottom: '8px',
                '&:last-child': {
                  marginBottom: 0,
                },
              },
              '& ol': { listStyle: 'revert', pl: { xs: 3, md: 5 }, margin: 0 },
              '& ul': { listStyle: 'revert', pl: { xs: 3, md: 5 } },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
          />
        )}
      </Box>

      {/* Promo banner — middle */}
      <PromoBannerSlot
        entityId={moduleId}
        position={PromoBannerPosition.MIDDLE}
        sx={{ width: { xs: '100%', md: 'calc(100% - 205px)' } }}
      />

      {/* Sections (Accordion Cards) */}
      <Box
        sx={{
          height: '100%',
          maxWidth: { xs: '100%', md: 'calc(100% - 205px)' },
        }}
      >
        {visibleSections.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '14px', md: '17px' } }}>
            {visibleSections.map((section, index) => (
              <SectionLoader
                key={section.id}
                section={section}
                index={index + 1}
                parentSpaceId={parentSpaceId}
                moduleId={moduleId}
                expandedSectionId={expandedSectionId}
                showSectionLabel={module.showSectionLabel !== false}
                showLessonLabel={module.showLessonLabel !== false}
              />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              bgcolor: 'white',
              borderRadius: '10px',
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Figtree", sans-serif',
                fontWeight: 400,
                fontSize: 14,
                color: 'rgba(0, 0, 0, 0.6)',
              }}
            >
              No sections available in this module.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Promo banner — footer */}
      <PromoBannerSlot
        entityId={moduleId}
        position={PromoBannerPosition.FOOTER}
        sx={{
          width: { xs: '100%', md: 'calc(100% - 205px)' },
          position: { xs: 'sticky', md: 'static' },
          bottom: { xs: 16, md: 'auto' },
          zIndex: { xs: theme.zIndex.appBar, md: 'auto' },
        }}
      />
    </Box>
  );
}
